/**
 * Type predicates and conversions — itoa/atoi/atof, fix/float, rtos/angtos
 * and their inverse parsers, plus `type`, `eval` and `apply`. rtos/angtos
 * implement the common LUNITS/AUNITS modes (decimal, scientific, fractional,
 * degrees); unit-sysvar defaults are documented in the help subset table.
 */

import { LispError } from '../errors';
import {
    NIL,
    mkInt,
    mkReal,
    mkStr,
    typeSymbol,
    type LispRuntimeApi,
    type LispValue,
} from '../values';
import { define, expectNumber } from './internal';
import { useSystemVarsStore } from '../../../state/useSystemVarsStore';

/** LUNITS/LUPREC — rtos defaults from the system-variable store. */
function rtosDefaults(): { mode: number; precision: number } {
    const vars = useSystemVarsStore.getState();
    const mode = vars.LUNITS >= 1 && vars.LUNITS <= 5 ? vars.LUNITS : 2;
    return { mode, precision: Math.max(0, Math.min(vars.LUPREC, 12)) };
}

/** AUNITS/AUPREC — angtos defaults from the system-variable store. */
function angtosDefaults(): { mode: number; precision: number } {
    const vars = useSystemVarsStore.getState();
    const mode = vars.AUNITS >= 0 && vars.AUNITS <= 4 ? vars.AUNITS : 0;
    return { mode, precision: Math.max(0, Math.min(vars.AUPREC, 12)) };
}

function fracToString(value: number, precision: number): string {
    // Approximate with a denominator up to 10^precision (AutoLISP mode 5).
    const maxDen = Math.pow(10, Math.min(precision, 6));
    let bestNum = 0;
    let bestDen = 1;
    let bestErr = Infinity;
    for (let den = 1; den <= maxDen; den++) {
        const num = Math.round(value * den);
        const err = Math.abs(value - num / den);
        if (err < bestErr) {
            bestErr = err;
            bestNum = num;
            bestDen = den;
            if (err === 0) break;
        }
    }
    const whole = Math.trunc(bestNum / bestDen);
    const num = bestNum - whole * bestDen;
    if (num === 0) return String(whole);
    const sign = value < 0 ? '-' : '';
    const frac = `${Math.abs(num)}/${bestDen}`;
    const absWhole = Math.abs(whole);
    return absWhole === 0 ? `${sign}${frac}` : `${sign}${absWhole} ${frac}`;
}

function rtosFormat(value: number, mode: number, precision: number): string {
    switch (mode) {
        case 1:
            return value.toExponential(Math.max(0, Math.min(precision, 12)));
        case 5:
            return fracToString(value, precision);
        case 3:
        case 4:
        // Engineering / architectural formats need a unit system the app does
        // not model yet — documented as decimal in the help subset table.
        // falls through
        default:
            return value.toFixed(Math.max(0, Math.min(precision, 12)));
    }
}

function angtosFormat(angleRad: number, mode: number, precision: number): string {
    const deg = (angleRad * 180) / Math.PI;
    switch (mode) {
        case 1: {
            const d = Math.floor(deg);
            const minutesFloat = (deg - d) * 60;
            const m = Math.floor(minutesFloat);
            const s = Math.round((minutesFloat - m) * 60);
            if (s === 60) return `${d}d${m + 1}'0"`;
            return `${d}d${m}'${s}"`;
        }
        case 2:
            return `${((angleRad * 200) / Math.PI).toFixed(Math.max(0, Math.min(precision, 12)))}g`;
        case 3:
            return angleRad.toFixed(Math.max(0, Math.min(precision, 12)));
        case 4:
        // Surveyor units — simplified to decimal degrees (help subset).
        // falls through
        default:
            return deg.toFixed(Math.max(0, Math.min(precision, 12)));
    }
}

export function registerConvert(rt: LispRuntimeApi): void {
    define(rt, 'itoa', (args) => {
        const v = args[0] ?? NIL;
        if (v.k !== 'int' && v.k !== 'real') throw new LispError('itoa: bad argument type');
        return mkStr(String(Math.trunc(v.v)));
    });
    define(rt, 'atoi', (args) => {
        const s = (args[0] ?? NIL).k === 'str' ? (args[0] as { v: string }).v : '';
        const m = /^[+-]?\d+/.exec(s.trim());
        return mkInt(m ? parseInt(m[0], 10) : 0);
    });
    define(rt, 'atof', (args) => {
        const s = (args[0] ?? NIL).k === 'str' ? (args[0] as { v: string }).v : '';
        const m = /^[+-]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?/.exec(s.trim());
        return mkReal(m ? Number(m[0]) : 0);
    });
    define(rt, 'fix', (args) => mkInt(Math.trunc(expectNumber(args[0] ?? NIL, 'fix'))));
    define(rt, 'float', (args) => mkReal(expectNumber(args[0] ?? NIL, 'float')));

    define(rt, 'rtos', (args) => {
        const value = expectNumber(args[0] ?? NIL, 'rtos');
        const defaults = rtosDefaults();
        const mode = args[1] && (args[1].k === 'int' || args[1].k === 'real') ? Math.trunc(args[1].v) : defaults.mode;
        const precision =
            args[2] && (args[2].k === 'int' || args[2].k === 'real') ? Math.trunc(args[2].v) : defaults.precision;
        return mkStr(rtosFormat(value, mode, precision));
    });
    define(rt, 'angtos', (args) => {
        const value = expectNumber(args[0] ?? NIL, 'angtos');
        const defaults = angtosDefaults();
        const mode = args[1] && (args[1].k === 'int' || args[1].k === 'real') ? Math.trunc(args[1].v) : defaults.mode;
        const precision =
            args[2] && (args[2].k === 'int' || args[2].k === 'real') ? Math.trunc(args[2].v) : defaults.precision;
        return mkStr(angtosFormat(value, mode, precision));
    });

    define(rt, 'distof', (args) => {
        const s = (args[0] ?? NIL).k === 'str' ? (args[0] as { v: string }).v.trim() : '';
        if (!s) return NIL;
        const mixed = /^([+-]?\d+)\s+(\d+)\/(\d+)$/.exec(s);
        if (mixed) {
            const whole = Number(mixed[1]);
            const den = Number(mixed[3]);
            if (den === 0) return NIL;
            const frac = Number(mixed[2]) / den;
            return mkReal(whole < 0 ? whole - frac : whole + frac);
        }
        const plain = /^([+-]?\d+)\/(\d+)$/.exec(s);
        if (plain) {
            const den = Number(plain[2]);
            if (den === 0) return NIL;
            return mkReal(Number(plain[1]) / den);
        }
        const v = Number(s);
        return Number.isFinite(v) ? mkReal(v) : NIL;
    });
    define(rt, 'angtof', (args) => {
        const s = (args[0] ?? NIL).k === 'str' ? (args[0] as { v: string }).v.trim() : '';
        if (!s) return NIL;
        const v = Number(s);
        return Number.isFinite(v) ? mkReal((v * Math.PI) / 180) : NIL;
    });

    define(rt, 'type', (args) => typeSymbol(args[0] ?? NIL));

    define(rt, 'eval', async (args, runtime) => runtime.evalValue(args[0] ?? NIL));

    define(rt, 'apply', async (args, runtime) => {
        const fn = args[0] ?? NIL;
        const list = args[1] ?? NIL;
        const argList = list.k === 'list' ? list.items : [];
        const resolved = fn.k === 'sym' ? runtime.globalEnv.lookup(fn.name) : fn;
        return runtime.apply(resolved, argList);
    });
}
